/**
 * A voice call's client secret, from the daemon (RFC 0307 3a).
 *
 * The daemon asks the llm-proxy with its own Mod login; the page sees only
 * the short-lived secret. With no daemon running there is no voice, and the
 * error says why.
 */

import { localServer } from '../../sdk/localServer'
import type { WindowBillingDispatch } from '../sharing/billingDispatch'
import type { VoiceProviderId, VoiceSecret } from './types'

export async function fetchVoiceSecret(
  opts: {
    server?: string
    fetch?: typeof fetch
    provider?: VoiceProviderId
    sessionId?: string
    billing?: WindowBillingDispatch
  } = {},
): Promise<VoiceSecret> {
  const fetchImpl = opts.fetch ?? fetch
  let res: Response
  if (opts.billing) {
    const { billing, sessionId } = opts
    if (!sessionId) throw new Error('Voice billing requires a session')
    // RFC 0345 S8: a billed OpenAI call is opened by the proxy from the
    // page's offer, so it can meter the call; the ticket is minted then.
    if ((opts.provider ?? 'openai') === 'openai')
      return {
        provider: 'openai',
        clientSecret: '',
        expiresAt: 0,
        model: '',
        voice: '',
        async exchangeSdp(offer) {
          const answer = await billing(sessionId, '/v1/realtime/session', {
            provider: 'openai',
            sdp: offer,
          })
          const data = await readAnswer(answer)
          if (typeof data.sdp !== 'string' || !data.sdp)
            throw new Error('Voice could not start: no answer came back')
          return data.sdp
        },
      }
    res = await billing(sessionId, '/v1/realtime/session', {
      provider: opts.provider,
    })
  } else {
    if (import.meta.env.VITE_MOD_KEYHIVE === '1')
      throw new Error('Voice requires an enrolled agent billing identity')
    try {
      res = await fetchImpl(`${opts.server ?? localServer()}/voice/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: opts.provider ?? 'openai' }),
      })
    } catch {
      throw new Error('Voice needs the Mod desktop app running')
    }
  }
  const data = await readAnswer(res)
  if (typeof data.clientSecret !== 'string')
    throw new Error('Voice could not start: no key came back')
  return data as VoiceSecret
}

async function readAnswer(
  res: Response,
): Promise<Partial<VoiceSecret> & { sdp?: unknown; error?: unknown }> {
  const data = (await res.json().catch(() => null)) as
    | (Partial<VoiceSecret> & { sdp?: unknown; error?: unknown })
    | null
  if (!res.ok) {
    throw new Error(
      typeof data?.error === 'string'
        ? data.error
        : data?.error &&
            typeof data.error === 'object' &&
            'message' in data.error &&
            typeof data.error.message === 'string'
          ? data.error.message
          : `Voice could not start (${res.status})`,
    )
  }
  return data ?? {}
}
